import { useEffect, useState } from 'react';
import Seo from '../../components/Seo/Seo';
import { Link, useNavigate } from 'react-router-dom';
import { useCartContext } from '../../context/CartContext';
import { useVehicle } from '../../context/VehicleContext';
import { createOrder, getAvailableDays, getAvailableHours, type TimeSlot } from '../../api/orders';
import { notifyCheckoutStarted } from '../../utils/orderFunnel';
import Gjkereso from '../../components/Gjkereso/Gjkereso';

const DELIVERY_FEE = 14500;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isBudapest(city: string, zip: string): boolean {
	const c = city.trim().toLowerCase();
	const z = parseInt(zip, 10);
	return c === 'budapest' || (!Number.isNaN(z) && z >= 1011 && z <= 1239);
}

export default function MegrendelesPage() {
	const { items, total, clearCart } = useCartContext();
	const v = useVehicle();
	const navigate = useNavigate();

	const battery = items[0];
	const vehicleId = v.selectedVehicleId;
	const carLabel = [
		v.brandOptions.find((o) => o.value === v.selectedBrand)?.label,
		v.typeOptions.find((o) => o.value === v.selectedType)?.label,
	].filter(Boolean).join(' ');

	const [form, setForm] = useState({ name: '', phone: '', email: '', zip: '', city: '', street: '', houseNumber: '', orderDay: '', orderTime: '' });
	const [payment, setPayment] = useState<'cash' | 'card'>('cash');
	const [days, setDays] = useState<TimeSlot[]>([]);
	const [hours, setHours] = useState<TimeSlot[]>([]);
	const [error, setError] = useState('');
	const [submitting, setSubmitting] = useState(false);
	// A peak day (capacity-bound, approved in Intel) carries a call-out surcharge: the API charges it,
	// so the summary shows it as soon as that day is picked.
	const peak = days.find((d) => d.value === form.orderDay)?.peak || 0;

	// Checkout opened → Order_Start funnel step (API dedups to one per session,
	// so a visitor who already fired it in the akkuválasztó is not re-counted).
	useEffect(() => {
		notifyCheckoutStarted();
	}, []);

	useEffect(() => {
		getAvailableDays().then((res) => {
			const d = res.data?.data || [];
			setDays(d);
			if (d.length) setForm((f) => ({ ...f, orderDay: d[0].value }));
		}).catch(() => setDays([]));
	}, []);

	useEffect(() => {
		if (!form.orderDay) { setHours([]); return; }
		getAvailableHours(form.orderDay)
			.then((res) => setHours(res.data?.data || []))
			.catch(() => setHours([]));
	}, [form.orderDay]);

	const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
		setForm((f) => ({ ...f, [k]: e.target.value }));

	async function submit(e: React.FormEvent) {
		e.preventDefault();
		setError('');
		if (!battery) { setError('Nincs kiválasztott akkumulátor.'); return; }
		if (!vehicleId) { setError('Kérjük, válaszd ki a gépjárműved a fenti kereső segítségével.'); return; }
		if (!form.name || !form.phone || !EMAIL_RE.test(form.email) || !form.zip || !form.city || !form.street) {
			setError('Kérjük, töltsd ki helyesen a kötelező mezőket (érvényes e-mail címmel).'); return;
		}
		if (!isBudapest(form.city, form.zip)) { setError('Online rendelés csak Budapest területén érhető el.'); return; }
		if (!form.orderDay || !form.orderTime) { setError('Kérjük, válassz kiszállítási napot és időpontot.'); return; }

		setSubmitting(true);
		try {
			const res = await createOrder({
				name: form.name, phone: form.phone, email: form.email,
				zip: form.zip, street: `${form.city}, ${form.street}`, house_number: form.houseNumber || '-',
				product_id: battery.productId, vehicle_id: vehicleId,
				order_day: form.orderDay, order_time: form.orderTime, billing_same: true,
			});
			if (res.data?.result === 'OK') {
				const summary = {
					orderNumber: res.data.order_number, orderId: res.data.order_id, switchToken: res.data.switch_token, payment,
					name: form.name, phone: form.phone, email: form.email,
					car: carLabel, address: `${form.zip} ${form.city}, ${form.street} ${form.houseNumber}`.trim(),
					day: days.find((d) => d.value === form.orderDay)?.label || form.orderDay,
					time: hours.find((h) => h.value === form.orderTime)?.label || form.orderTime,
					battery: battery.name, price: battery.price, deliveryFee: DELIVERY_FEE + peak,
					total: res.data.total ?? total + peak,
				};
				localStorage.setItem('akku24_last_order', JSON.stringify(summary));
				clearCart();
				navigate('/megrendeles/sikeres');
			} else {
				setError(res.data?.message || 'Hiba történt a rendelés leadásakor.');
			}
		} catch {
			setError('Hiba történt a rendelés leadásakor. Kérjük próbáld újra, vagy hívd ügyfélszolgálatunkat: 06 30 626 6000.');
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<div className="order">
			<Seo title="Megrendelés - Akku 24+" noindex />
			<div className="container">
				<div className="order__head"><h1>Megrendelés</h1></div>

				{!battery && (
					<p className="note">Még nincs kiválasztott akkumulátor. Nézd meg az <Link to="/arlista">árlistát</Link> vagy használd az <Link to="/akkuvalaszto">akkuválasztót</Link>.</p>
				)}

				{battery && !vehicleId && (
					<>
						<p className="note" style={{ textAlign: 'center' }}>A rendelés leadásához kérjük, válaszd ki a gépjárműved:</p>
						<Gjkereso prompt="Válaszd ki gépjárműved típusát:" target="/megrendeles" />
					</>
				)}

				{battery && vehicleId && (
					<form className="order__layout" onSubmit={submit}>
						<div className="order__box">
							<h2>Megrendelői és kiszállítási adatok</h2>
							{error && <div className="order__error">{error}</div>}
							<div className="order__note">A kiszállítás és helyszíni csere Budapest területén érhető el.</div>

							<div className="field"><label htmlFor="order-name">Név *</label><input id="order-name" name="name" autoComplete="name" value={form.name} onChange={set('name')} required /></div>
							<div className="field-row">
								<div className="field"><label htmlFor="order-phone">Telefonszám *</label><input id="order-phone" name="phone" type="tel" autoComplete="tel" inputMode="tel" value={form.phone} onChange={set('phone')} required /></div>
								<div className="field"><label htmlFor="order-email">E-mail *</label><input id="order-email" name="email" type="email" autoComplete="email" value={form.email} onChange={set('email')} required /></div>
							</div>
							<div className="field-row">
								<div className="field"><label htmlFor="order-zip">Irányítószám *</label><input id="order-zip" name="zip" autoComplete="postal-code" inputMode="numeric" value={form.zip} onChange={set('zip')} required /></div>
								<div className="field"><label htmlFor="order-city">Város *</label><input id="order-city" name="city" autoComplete="address-level2" value={form.city} onChange={set('city')} required /></div>
							</div>
							<div className="field-row">
								<div className="field"><label htmlFor="order-street">Utca *</label><input id="order-street" name="street" autoComplete="address-line1" value={form.street} onChange={set('street')} required /></div>
								<div className="field"><label htmlFor="order-house">Házszám</label><input id="order-house" name="house_number" autoComplete="address-line2" value={form.houseNumber} onChange={set('houseNumber')} /></div>
							</div>
							<div className="field-row">
								<div className="field">
									<label htmlFor="order-day">Kiszállítás napja *</label>
									<select id="order-day" name="order_day" autoComplete="off" value={form.orderDay} onChange={set('orderDay')} required>
										{days.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
									</select>
								</div>
								<div className="field">
									<label htmlFor="order-time">Időpont *</label>
									<select id="order-time" name="order_time" autoComplete="off" value={form.orderTime} onChange={set('orderTime')} required disabled={!hours.length}>
										<option value="">Válassz időpontot</option>
										{hours.map((h) => <option key={h.value} value={h.value}>{h.label}</option>)}
									</select>
								</div>
							</div>

							<button type="submit" className="btn" disabled={submitting}>
								{submitting ? 'Feldolgozás…' : 'Megrendelés leadása'}
							</button>
						</div>

						<aside className="order__box order__summary">
							<h2>Összesítő</h2>
							<div className="summary-item"><span className="label">Jármű</span><span>{carLabel || '—'}</span></div>
							<div className="summary-item"><span className="label">Akkumulátor</span><span>{battery.name}</span></div>
							<div className="summary-row"><span>Akkumulátor ára</span><span>{Number(battery.price).toLocaleString('hu-HU')} Ft</span></div>
							<div className="summary-row"><span>Kiszállási és szerelési díj{peak > 0 ? ` (benne csúcsnap +${peak.toLocaleString('hu-HU')} Ft)` : ''}</span><span>{(DELIVERY_FEE + peak).toLocaleString('hu-HU')} Ft</span></div>
							<div className="summary-row summary-total"><span>Összesen</span><span>{(Number(total) + peak).toLocaleString('hu-HU')} Ft</span></div>

							<div className="pay-select">
								<span className="label">Fizetési mód a helyszínen</span>
								<label className="pay-opt">
									<input type="radio" name="payment" checked={payment === 'cash'} onChange={() => setPayment('cash')} />
									<span>Készpénz</span>
								</label>
								<label className="pay-opt">
									<input type="radio" name="payment" checked={payment === 'card'} onChange={() => setPayment('card')} />
									<span>Bankkártya</span>
								</label>
							</div>
							<p className="pay-note">A fizetés a helyszínen, a szerelőnél történik a csere után — készpénzzel vagy kártyával.</p>
						</aside>
					</form>
				)}
			</div>
		</div>
	);
}
